/**
 * FAVORITE SELLER BUTTON
 * 
 * A reusable button to favorite/unfavorite a seller.
 * Can be used on:
 * - User profile pages
 * - Marketplace cards
 * - Anywhere you want to show a follow/favorite option
 * 
 * Props:
 * - sellerId: The user ID of the seller to favorite
 * - sellerUsername: Username for display and storage
 * - sellerAvatar: Avatar URL for storage
 * - currentUserId: The logged-in user's ID
 * - variant: 'icon' | 'button' | 'large' - display style
 */

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Heart, Loader2 } from 'lucide-react';

interface FavoriteSellerButtonProps {
  sellerId: string;
  sellerUsername?: string | null;
  sellerAvatar?: string | null;
  currentUserId: string | null;
  variant?: 'icon' | 'button' | 'large';
  onToggle?: (isFavorited: boolean) => void;
}

const FavoriteSellerButton: React.FC<FavoriteSellerButtonProps> = ({
  sellerId,
  sellerUsername,
  sellerAvatar,
  currentUserId,
  variant = 'icon',
  onToggle
}) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  // Check if seller is already favorited
  useEffect(() => {
    const checkFavorite = async () => {
      if (!currentUserId || !sellerId || currentUserId === sellerId) {
        setChecking(false);
        return;
      }
      
      try {
        // Check specific document using sellerId as document ID
        const favoriteRef = doc(db, 'users', currentUserId, 'favoriteSellers', sellerId);
        const favoriteDoc = await getDoc(favoriteRef);
        setIsFavorited(favoriteDoc.exists());
      } catch (err) {
        console.error('Error checking favorite status:', err);
      } finally {
        setChecking(false);
      }
    };

    checkFavorite();
  }, [currentUserId, sellerId]);

  // Toggle favorite
  const handleToggle = async () => {
    if (!currentUserId) {
      alert('Please sign in to favorite sellers');
      return;
    }

    if (currentUserId === sellerId) {
      return; // Can't favorite yourself
    }

    setLoading(true);
    try {
      // Use sellerId as the document ID to prevent duplicates
      const favoriteRef = doc(db, 'users', currentUserId, 'favoriteSellers', sellerId);
      
      if (isFavorited) {
        // Remove from favorites
        await deleteDoc(favoriteRef);
        setIsFavorited(false);
        onToggle?.(false);
      } else {
        // Add to favorites using setDoc (overwrites if exists, preventing duplicates)
        await setDoc(favoriteRef, {
          sellerId,
          sellerUsername: sellerUsername || '',
          sellerAvatar: sellerAvatar || null,
          addedAt: serverTimestamp()
        });
        setIsFavorited(true);
        onToggle?.(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert('Failed to update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Don't show if it's the user's own profile or not logged in for certain variants
  if (currentUserId === sellerId) {
    return null;
  }

  // Icon variant - small heart icon
  if (variant === 'icon') {
    if (!currentUserId) return null;
    
    return (
      <button
        onClick={handleToggle}
        disabled={loading || checking}
        className={`p-1.5 rounded-full transition-all ${
          isFavorited
            ? 'text-red-500'
            : 'text-gray-300 hover:text-red-400'
        }`}
        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        {loading || checking ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
        )}
      </button>
    );
  }

  // Button variant - medium sized button with text
  if (variant === 'button') {
    return (
      <button
        onClick={handleToggle}
        disabled={loading || checking || !currentUserId}
        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
          isFavorited
            ? 'bg-red-100 text-red-600 hover:bg-red-200'
            : !currentUserId
              ? 'bg-slate-800/40 text-glass-muted cursor-not-allowed'
              : 'bg-white border-2 border-red-400 text-red-500 hover:bg-red-50'
        }`}
        title={!currentUserId ? 'Sign in to favorite sellers' : undefined}
      >
        {loading || checking ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isFavorited ? (
          <>
            <Heart className="icon-sm fill-current" />
            Following
          </>
        ) : (
          <>
            <Heart className="icon-sm" />
            Follow
          </>
        )}
      </button>
    );
  }

  // Large variant - prominent button for profile pages
  if (variant === 'large') {
    return (
      <button
        onClick={handleToggle}
        disabled={loading || checking || !currentUserId}
        className={`w-full px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
          isFavorited
            ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg hover:shadow-xl'
            : !currentUserId
              ? 'bg-gray-200 text-glass-muted cursor-not-allowed'
              : 'bg-white border-2 border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500'
        }`}
        title={!currentUserId ? 'Sign in to favorite sellers' : undefined}
      >
        {loading || checking ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading...
          </>
        ) : isFavorited ? (
          <>
            <Heart className="icon-md fill-current" />
            Following This Seller
          </>
        ) : (
          <>
            <Heart className="icon-md" />
            Follow This Seller
          </>
        )}
      </button>
    );
  }

  return null;
};

export default FavoriteSellerButton;
